import { useCallback, useRef } from 'react';
import { useChatStore } from '@/store/useChatStore';
import { useStudioStore } from '@/store/useStudioStore';
import { streamingManager } from '@/lib/StreamingManager';
import { pollPreviewReady } from '@/lib/previewUtils';
import { buildDraftPreviewHtml } from '@/lib/domUtils';
import {
  getToolCallMessageContent,
  parseMaybeJsonContent,
  parseErrorString,
  removeChinese,
  isRoleDescription,
} from '@/lib/agentChatUtils';
import {
  ProtocolWrapper,
  InboundData,
  MessageContent,
  CreationSuggestionsResponse,
  ExpertReviewResponse,
  CreationSuggestionItem,
  ToolCallResponse,
} from '@/types/protocol';
import { getExpertReviewSummary, useExpertStore } from '@/store/useExpertStore';
import { useCreatorStore } from '@/store/useCreatorStore';
import { useChatService } from '@/hooks/studio/useChatService';
import { nanoid } from 'nanoid';
import { trackEvent } from '@/api/track';
import {
  clearRememberedLegacyBuildStart,
  getRememberedLegacyBuildStart,
} from '@/lib/generationTiming';
import { isSpecialProjectType } from '@/lib/projectUtils';
import { loadSpecialProjectPreviewHtml } from '@/lib/specialProjectPreview';
import { notifyPointBalanceChanged } from '@/lib/creditBalance';

/**
 * useChatInbound - 下行消息处理 Hook
 * 职责：解析从底层 Socket 接收到的各种原始数据包，并分发到状态管理 or 流式管理器。
 */
export function useChatInbound() {
  const {
    setIsGenerating,
    setStatus,
    appendMessage,
    updateMessageById,
    setIsPreviewReady,
    setBuildError,
  } = useChatStore();
  const {
    currentProjectId,
    currentProjectTypeId,
    isBuilding,
    setIsBuilding,
    setIframeUrl,
    setGeneratedHtml,
    studioMode,
    setStudioMode,
  } = useStudioStore();
  const {
    setReviewing,
    setItems,
    setActiveIndex,
    setOverallText,
    setProjectId,
    completeLatestReviewSession,
  } = useExpertStore();
  const {
    setCreating,
    setWordsItems,
    setLookItems,
    setPartsItems,
    setImpeccableItems,
    setInfoItems,
    setProjectId: setCreatorProjectId,
  } = useCreatorStore();
  const { sendCommand } = useChatService();
  const buildStartTimeRef = useRef<number>(0);

  const resolveRememberedBuildStart = useCallback(
    (sessionId?: string | null) =>
      getRememberedLegacyBuildStart(currentProjectId, sessionId) ||
      getRememberedLegacyBuildStart(currentProjectId, null),
    [currentProjectId],
  );

  const markBuildStarted = useCallback(
    (sessionId?: string | null) => {
      if (buildStartTimeRef.current <= 0) {
        buildStartTimeRef.current = resolveRememberedBuildStart(sessionId) || Date.now();
      }
    },
    [resolveRememberedBuildStart],
  );

  const clearBuildStarted = useCallback(
    (sessionId?: string | null) => {
      buildStartTimeRef.current = 0;
      clearRememberedLegacyBuildStart(currentProjectId, sessionId);
      clearRememberedLegacyBuildStart(currentProjectId, null);
    },
    [currentProjectId],
  );

  const ensureBuildStartedForCompletion = useCallback(
    (sessionId?: string | null) => {
      if (buildStartTimeRef.current <= 0) {
        buildStartTimeRef.current = resolveRememberedBuildStart(sessionId) || Date.now();
      }
    },
    [resolveRememberedBuildStart],
  );

  /**
   * 触发预览就绪检测
   */
  const triggerPreviewCheck = useCallback(
    (sessionId?: string | null) => {
      if (!currentProjectId) return;
      const startTime = buildStartTimeRef.current;
      pollPreviewReady(
        currentProjectId,
        async (iframeUrl) => {
          const generationTime = startTime > 0 ? Math.round((Date.now() - startTime) / 1000) : 0;
          clearBuildStarted(sessionId);
          const localPreviewHtml = isSpecialProjectType(currentProjectTypeId)
            ? await loadSpecialProjectPreviewHtml(currentProjectId)
            : '';
          setGeneratedHtml(localPreviewHtml ? buildDraftPreviewHtml(localPreviewHtml) : '');
          setIframeUrl(localPreviewHtml ? null : iframeUrl);
          setIsBuilding(false);
          setIsPreviewReady(true);
          notifyPointBalanceChanged();
          trackEvent({
            eventCode: 'app_gen_success',
            eventData: JSON.stringify({ generation_time: generationTime }),
          });
          if (studioMode === '') {
            setStudioMode('expert');
          }
        },
        (errorMsg) => {
          clearBuildStarted(sessionId);
          setIsBuilding(false);
          setBuildError(true, errorMsg);
        },
      );
    },
    [
      currentProjectId,
      currentProjectTypeId,
      setGeneratedHtml,
      setIframeUrl,
      setIsBuilding,
      setIsPreviewReady,
      setStudioMode,
      studioMode,
      setBuildError,
      clearBuildStarted,
    ],
  );

  /**
   * 处理专家评审数据
   */
  const processExpertReview = useCallback(
    (reviewData: ExpertReviewResponse) => {
      const { suggestions, overall_text } = reviewData;
      const mappedSuggestions =
        suggestions?.map((item) => ({
          ...item,
          icon: item.typeName,
        })) || [];

      if (currentProjectId) setProjectId(currentProjectId);
      setOverallText(overall_text || '');
      setReviewing(false);
      setItems(mappedSuggestions);
      setActiveIndex(mappedSuggestions.length > 0 ? 0 : -1);
      completeLatestReviewSession({
        projectId: currentProjectId ?? null,
        summary: getExpertReviewSummary(overall_text, mappedSuggestions),
        suggestionCount: mappedSuggestions.length,
      });
    },
    [
      setOverallText,
      setReviewing,
      setItems,
      setActiveIndex,
      setProjectId,
      currentProjectId,
      completeLatestReviewSession,
    ],
  );

  /**
   * 处理创作建议数据
   */
  const processCreationSuggestions = useCallback(
    (suggestions: CreationSuggestionsResponse) => {
      const mapSuggestions = (items: CreationSuggestionItem[]) => {
        return items.map((item) => ({
          id: `sug-${nanoid()}`,
          icon: item.icon,
          label: item.label,
          prompt: item.prompt,
        }));
      };

      if (currentProjectId) setCreatorProjectId(currentProjectId);

      if (suggestions.words?.length) setWordsItems(mapSuggestions(suggestions.words));
      if (suggestions.look?.length) setLookItems(mapSuggestions(suggestions.look));
      if (suggestions.parts?.length) setPartsItems(mapSuggestions(suggestions.parts));
      if (suggestions.impeccable?.length)
        setImpeccableItems(mapSuggestions(suggestions.impeccable));
      if (suggestions.info?.length) setInfoItems(mapSuggestions(suggestions.info));

      setCreating(false);
    },
    [
      setWordsItems,
      setLookItems,
      setPartsItems,
      setImpeccableItems,
      setInfoItems,
      setCreating,
      setCreatorProjectId,
      currentProjectId,
    ],
  );

  /**
   * 核心处理入口
   */
  const handleInboundMessage = useCallback(
    (rawPayload: ProtocolWrapper<InboundData>) => {
      const { data, projectId, sessionId: wrapperSessionId } = rawPayload || {};

      // 过滤当前项目以外的消息
      if (projectId != currentProjectId) return;

      if (!data || !data.type) return;

      const messageSessionId = data.sessionId ?? wrapperSessionId;

      if (data.type !== 'complete') {
        markBuildStarted(messageSessionId);
      }

      if (!isBuilding) {
        setIsBuilding(true);
      }

      switch (data.type) {
        case 'agent-message-ack':
          markBuildStarted(messageSessionId);
          setIsGenerating(true);
          setIsPreviewReady(false);
          break;

        case 'message':
          if (data.role === 'assistant' && Array.isArray(data.content)) {
            data.content.forEach((item: MessageContent) => {
              if (item.type === 'text') {
                const errorInfo = parseErrorString(item.text);
                if (errorInfo.isError) {
                  setBuildError(true, errorInfo.message || item.text);
                  return;
                }

                // 解析内容中的 JSON 块
                const { cleanText, parsed } = parseMaybeJsonContent(item.text);

                // 直接分发解析出的数据
                if (parsed?.type === 'expert_review') {
                  processExpertReview(parsed as unknown as ExpertReviewResponse);
                } else if (parsed?.type === 'creation_suggestions') {
                  processCreationSuggestions(parsed as unknown as CreationSuggestionsResponse);
                } else if (cleanText) {
                  // 过滤中文与 Role Description
                  const filteredText = removeChinese(cleanText);
                  if (filteredText.trim() && !isRoleDescription(filteredText)) {
                    // 如果内容是特定的提示，则自动发送继续指令
                    if (
                      filteredText.includes(
                        'Todos have been modified successfully. Ensure that you continue to use the todo list to track your progress. Please proceed with the current tasks if applicable',
                      )
                    ) {
                      sendCommand('Please proceed with the current tasks.', { hidden: true });
                    }

                    appendMessage({
                      id: data.messageId || `msg-${nanoid()}`,
                      role: 'assistant',
                      content: filteredText,
                      timestamp: new Date(),
                    });
                  }
                }
              }
            });
          }
          break;

        case 'tool-use':
        case 'tool-call': {
          const toolData = data as ToolCallResponse;
          const content = getToolCallMessageContent(toolData.toolName, toolData.toolInput);
          const filteredContent = removeChinese(content);
          appendMessage({
            id: toolData.toolUseId,
            toolUseId: toolData.toolUseId,
            role: 'system',
            content: filteredContent,
            timestamp: new Date(),
          });
          break;
        }

        case 'tool-result':
          updateMessageById(data.toolUseId, (msg) => ({ ...msg, toolStatus: 'done' }));
          if (Array.isArray(data.content)) {
            const resultText = data.content.map((c: MessageContent) => c.text).join('\n');
            streamingManager.appendCollapsibleOutput(resultText, Date.now());
          }
          break;

        case 'complete': {
          ensureBuildStartedForCompletion(messageSessionId);
          streamingManager.finishAll();
          setIsGenerating(false);
          setCreating(false);
          setReviewing(false);
          setStatus(null);
          triggerPreviewCheck(messageSessionId);
          break;
        }

        case 'agent-error':
        case 'error': {
          clearBuildStarted(messageSessionId);
          const rawContent = data.message || 'An unknown error occurred';
          const errorInfo = parseErrorString(rawContent);
          const finalMessage =
            errorInfo.isError && errorInfo.code
              ? `Error ${errorInfo.code}: ${errorInfo.message}`
              : rawContent;
          setBuildError(true, finalMessage);
          break;
        }
      }
    },
    [
      setIsGenerating,
      setStatus,
      appendMessage,
      updateMessageById,
      setIsPreviewReady,
      setBuildError,
      triggerPreviewCheck,
      markBuildStarted,
      ensureBuildStartedForCompletion,
      clearBuildStarted,
      processCreationSuggestions,
      processExpertReview,
      sendCommand,
      isBuilding,
      setIsBuilding,
      setCreating,
      setReviewing,
      currentProjectId,
    ],
  );

  return { handleInboundMessage };
}
